import { useState } from 'react';
import { submitFeedback, CreateFeedbackRequest } from '../services/feedback';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Rating from '@mui/material/Rating';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import FeedbackIcon from '@mui/icons-material/Feedback';
import StarIcon from '@mui/icons-material/Star';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const ratingLabels: { [key: number]: string } = {
  1: 'Velmi špatné',
  2: 'Špatné',
  3: 'Průměrné',
  4: 'Dobré',
  5: 'Výborné',
};

export default function Feedback() {
  const [rating, setRating] = useState<number | null>(null);
  const [hoverRating, setHoverRating] = useState<number>(-1);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!rating) {
      setError('Prosím vyberte hodnocení.');
      return;
    }

    if (!message.trim()) {
      setError('Prosím napište zprávu.');
      return;
    }

    if (message.length > 2000) {
      setError('Zpráva může mít maximálně 2000 znaků.');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const request: CreateFeedbackRequest = {
        rating: rating,
        message: message.trim(),
      };

      await submitFeedback(request);
      setSuccess(true);
    } catch (err: unknown) {
      console.error('Error submitting feedback:', err);
      const apiError = err as { details?: unknown; message?: string };
      
      // Extract error message from various possible formats
      let errorMessage = 'Nepodařilo se odeslat zpětnou vazbu. Zkuste to prosím znovu.';
      if (typeof apiError.details === 'string') {
        errorMessage = apiError.details;
      } else if (apiError.details && typeof apiError.details === 'object') {
        const details = apiError.details as { error?: string; message?: string; title?: string };
        errorMessage = details.error || details.message || details.title || errorMessage;
      } else if (typeof apiError.message === 'string') {
        errorMessage = apiError.message;
      }
      
      setError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setRating(null);
    setMessage('');
    setSuccess(false);
    setError(null);
  };

  if (success) {
    return (
      <Box sx={{ flexGrow: 1, p: 3, display: 'flex', justifyContent: 'center' }}>
        <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom>
            Děkujeme za zpětnou vazbu!
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Vaše zpětná vazba nám pomáhá zlepšovat naši aplikaci.
          </Typography>
          <Button variant="contained" onClick={handleReset}>
            Odeslat další zpětnou vazbu
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <FeedbackIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4" component="h1">
            Zpětná vazba
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Pomozte nám zlepšit naši aplikaci
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 4, maxWidth: 600 }}>
        <form onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom>
              Jak hodnotíte naši aplikaci?
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Rating
                name="rating"
                value={rating}
                onChange={(_, newValue) => setRating(newValue)}
                onChangeActive={(_, newHover) => setHoverRating(newHover)}
                size="large"
                emptyIcon={<StarIcon style={{ opacity: 0.55 }} fontSize="inherit" />}
              />
              {rating !== null && (
                <Typography variant="body1" color="text.secondary">
                  {ratingLabels[hoverRating !== -1 ? hoverRating : rating]}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <TextField
              label="Vaše zpráva"
              multiline
              rows={4}
              fullWidth
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Napište nám, co se vám líbí, co bychom mohli zlepšit, nebo jaké funkce byste chtěli přidat..."
              helperText={`${message.length}/2000 znaků`}
              error={message.length > 2000}
            />
          </Box>

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitting || !rating || !message.trim()}
          >
            {submitting ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} color="inherit" />
                Odesílám...
              </>
            ) : (
              'Odeslat zpětnou vazbu'
            )}
          </Button>
        </form>
      </Paper>
    </Box>
  );
}
